import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { omit, toMerged } from 'es-toolkit';

import { GeometryClassDefinition } from '../../../custom-shared/types/editor/classes';
import { GeometryClassInstance } from '../../../custom-shared/types/editor/instances';

import { createProjectFile, exitEditor } from './project.actions';
import {
  loadCloudProject,
  loadLocalProject,
  patchProjectFile,
  refreshProjectFile,
} from './project.thunks';
import { ProjectSliceState } from './types';

import './project.middlewares';

const initialState: ProjectSliceState = {
  project: null,
  error: null,
  files: {},
  definitionFiles: {},
};

export const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(createProjectFile, (state, action) => {
      const { type, file } = action.payload;
      if (type === 'definition') {
        state.definitionFiles[file.content.name] = file;
      } else {
        if (!state.files[file.baseName]) {
          state.files[file.baseName] = {} as any;
        }

        state.files[file.baseName]!.json = file;
      }
    });
    builder.addCase(exitEditor, () => initialState);
    builder.addCase(patchProjectFile.fulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }

      const { type, baseName, content } = action.payload;

      if (type === 'definition' && state.definitionFiles[baseName]) {
        state.definitionFiles[baseName].content =
          content as GeometryClassDefinition;
      } else if (state.files[baseName] && state.files[baseName].json) {
        state.files[baseName].json.content = content as GeometryClassInstance;
      }
    });
    builder.addCase(refreshProjectFile.fulfilled, (state, action) => {
      return toMerged(state, omit(action.payload, ['project']));
    });
    builder.addMatcher(
      isAnyOf(loadLocalProject.fulfilled, loadCloudProject.fulfilled),
      (state, action) => {
        return {
          ...state,
          ...action.payload,
        };
      },
    );
    builder.addMatcher(
      isAnyOf(loadCloudProject.rejected, loadLocalProject.rejected),
      (state, action) => {
        state.error = action.error.message || 'alerts.cloudProjectError';
      },
    );
  },
});

export const projectReducer = projectSlice.reducer;
